import { Box, Button, CloseButton, Flex, Text, type ButtonProps } from '@chakra-ui/react';
import type { ReactNode } from 'react';

/**
 * StatusBanner — the project's one notice/banner UI. Replaces Chakra's
 * heavier <Alert> with a softer modern style: thin accent bar on the
 * left, tinted background, title + optional description + optional
 * action button. The banner's tone is communicated by the left border
 * and tint alone — we deliberately omit an icon because a colour-coded
 * callout reads cleaner at small sizes than icon + colour duplication.
 *
 * Tone maps to semantic tokens so light/dark both work:
 *
 *   tone='info'    →  bg.info / border.info / text.info   (brand navy tint)
 *   tone='warning' →  bg.warning / border.warning / text.warning
 *   tone='error'   →  bg.error / border.error / text.error
 *   tone='success' →  bg.success / border.success / text.success
 *
 * Reach for this whenever you'd otherwise reach for Chakra's <Alert>. Use
 * inside pages, settings tabs, and dialogs. For transient notifications
 * (< ~5s) use useAppToast() instead.
 */

export type StatusBannerTone = 'info' | 'warning' | 'error' | 'success';

/*
 * WHY THE ACTION BUTTON DOES NOT TAKE THE BANNER'S TONE.
 *
 * This used to compute `colorScheme` from `tone` — `'accent' | 'red' | 'yellow'`
 * — on a `variant="outline"` button, and all three were INERT, because
 * `outline` is declared in `theme/components.button.ts` and a declared object
 * wins the `extendTheme` merge before Chakra's colorScheme machinery runs. So
 * the action has always rendered neutral in all four tones.
 *
 * The obvious fix was to map the tone onto the matching intent variant. That is
 * WRONG, and making the dead code live is what showed it: the tone describes the
 * SITUATION, the button describes the ACTION, and deriving one from the other is
 * the same category error as putting `colorScheme="red"` on a retry button. All
 * five live `action` sites are `tone="warning"` or `tone="error"` with a
 * RECOVERY label — "Reload", "Retry", "Try again", "Download original". None
 * destroys anything, and `retry` is on the intent guard's own never-destructive
 * list. An amber or red Reload button would be telling the user the wrong thing.
 *
 * So the action stays on the neutral `outline` it has always rendered. The tone
 * is carried by the banner's tint and left border, which were correct all along.
 * A banner whose action really is destructive should pass
 * `action.variant="outline-danger"` explicitly, at the call site, where someone
 * can see what the button does.
 */

export interface StatusBannerAction {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  variant?: ButtonProps['variant'];
}

interface StatusBannerProps {
  tone: StatusBannerTone;
  title: ReactNode;
  description?: ReactNode;
  action?: StatusBannerAction;
  onDismiss?: () => void;
}

export function StatusBanner({ tone, title, description, action, onDismiss }: StatusBannerProps) {
  const bg = `bg.${tone}`;
  const border = `border.${tone}`;
  // error + warning are assertive live regions (screen readers interrupt);
  // info + success are polite (announced at next natural pause). Matches
  // WCAG 2.1 ARIA live-region patterns — this banner is the primary error
  // UI across login, reset-password, verify-email, OAuth, and error
  // boundaries, so getting this right affects all of those.
  const isAssertive = tone === 'error' || tone === 'warning';

  return (
    <Box
      role={isAssertive ? 'alert' : 'status'}
      aria-live={isAssertive ? 'assertive' : 'polite'}
      bg={bg}
      borderLeft="3px solid"
      borderLeftColor={border}
      borderRadius="lg"
      px={4}
      py={3}
    >
      <Flex align="center" gap={3} flexWrap="wrap">
        <Box flex="1" minW={0}>
          <Text fontWeight="semibold" fontSize="sm" color="text.primary" lineHeight="short">
            {title}
          </Text>
          {description && (
            <Text fontSize="sm" color="text.secondary" mt={0.5} lineHeight="base">
              {description}
            </Text>
          )}
        </Box>

        {action && (
          <Button
            size="sm"
            variant={action.variant ?? 'outline'}
            onClick={action.onClick}
            isLoading={action.isLoading}
            flexShrink={0}
          >
            {action.label}
          </Button>
        )}

        {onDismiss && (
          <CloseButton
            size="sm"
            onClick={onDismiss}
            color="text.secondary"
            flexShrink={0}
            aria-label="Dismiss"
          />
        )}
      </Flex>
    </Box>
  );
}
